/**
 * ─────────────────────────────────────────────────────────────────────────────────
 *  [C5c §2] ONE PERSON, MANY DUTY POINTS — THE PURE HALF OF A SHARED COLUMN.
 *
 *  A shared column lives on ONE card, its ANCHOR, and every other card of the order carries no
 *  line for it at all. That is what makes "an inspector covering five duty points" one count
 *  rather than five: every total in the system is a plain sum over cards or duties, so the only
 *  way a column is counted once is to exist once. Everything else — the dash on a covered card,
 *  the read-only names on its fill sheet, the covered rows in Duty Mitra — is a READ-SIDE rule
 *  derived from `arrangements.shared_columns`, and this file is the part of that rule both the
 *  server and the builder can call.
 *
 *  ⚠️ COVERAGE JOINS ON THE BUSINESS DAY AND THE INSTANTS, NEVER ON A SLOT KEY. A shift id is
 *  minted PER CARD, so two cards running 08:00–20:00 on one day carry two different slot keys;
 *  only a whole-day duty's `<date>#day` coincides. "Which anchor duty covers this one" is
 *  therefore "which anchor duties are on the same `duty_date` and meet its window".
 *
 *  ⚠️ NAMED `coverage…` AND `instantsMeet`, NEVER `overlapping…`. `conflictDeferred.test.ts`
 *  CLAIM 1 reserves the vocabulary a conflict feature would use, across both packages, and a
 *  function here is not a conflict: covering several places at once is the POINT of this
 *  feature (§2k).
 *
 *  ⚠️ NO LUXON AND NO DATE ARITHMETIC. The instants are already instants; comparing them needs
 *  `Date.parse` and nothing else, so the file is safe to import anywhere `@pams/shared/utils` is.
 * ─────────────────────────────────────────────────────────────────────────────────
 */

/**
 * `P:<postTypeId>`, `V:<vehicleTypeId>` or `A:<slug>` — exactly the keys `lineKeyOf` mints.
 *
 * ⚠️ A SHARED COLUMN IS NAMED BY ITS KEY ON THE WIRE, SO THE KEY IS VALIDATED LIKE AN ID. A
 * `?` or an `A:` with nothing after it is a degenerate key `lineKeyOf` produces only for a line
 * the schema already refuses, and a shared entry naming one would name nothing.
 */
export const LINE_KEY_REGEX = /^(?:[PV]:[0-9a-f]{24}|A:[a-z0-9]+(?:_[a-z0-9]+)*)$/;

/** An instant as either side of the codebase holds it — an ISO string on the wire, a `Date` in a model. */
type Instant = string | Date;

/** One duty, as coverage needs it: the business day and its window. */
export type CoverageWindow = { dutyDate: string; startsAt: Instant; endsAt: Instant };

export type Coverage = 'FULL' | 'PARTIAL' | 'NONE';

const ms = (value: Instant): number => (typeof value === 'string' ? Date.parse(value) : value.getTime());

/**
 * Whether two windows share any instant. Half-open, `[start, end)`, which is how `ends_at` is
 * stored — a duty ending at 20:00 and one starting at 20:00 do NOT meet, because nobody stands
 * both at once.
 */
export const instantsMeet = (
  left: { startsAt: Instant; endsAt: Instant },
  right: { startsAt: Instant; endsAt: Instant },
): boolean => ms(left.startsAt) < ms(right.endsAt) && ms(right.startsAt) < ms(left.endsAt);

/**
 * The anchor duties that cover one duty — on its business day, meeting its window — earliest
 * first. A stable order, so "which anchor does this mirror" has one answer on every read.
 */
export const coveringAnchors = <T extends CoverageWindow>(duty: CoverageWindow, anchors: readonly T[]): T[] =>
  anchors
    .filter((anchor) => anchor.dutyDate === duty.dutyDate && instantsMeet(anchor, duty))
    .sort((left, right) => ms(left.startsAt) - ms(right.startsAt));

/**
 * ─────────────────────────────────────────────────────────────────────────────────
 *  HOW MUCH OF ONE DUTY ITS ANCHOR DUTIES COVER — THE PUBLISH BLOCKER AND THE WARNING.
 *
 *  `NONE`: no anchor duty on that day meets it, so the column is asked of nobody for the whole
 *  of this duty — a person the order says is covered has nobody covering them. A BLOCKER.
 *
 *  `PARTIAL`: something meets it but the merged anchor windows, clipped to this duty, leave a
 *  gap. A night shift anchored on a day shift is the ordinary case. A WARNING, because the
 *  operator may mean exactly that.
 *
 *  `FULL`: the merged anchor windows span `[startsAt, endsAt)` without a gap.
 * ─────────────────────────────────────────────────────────────────────────────────
 */
export const coverageOf = (duty: CoverageWindow, anchors: readonly CoverageWindow[]): Coverage => {
  const start = ms(duty.startsAt);
  const end = ms(duty.endsAt);
  const meeting = coveringAnchors(duty, anchors).map(
    (anchor) => [Math.max(start, ms(anchor.startsAt)), Math.min(end, ms(anchor.endsAt))] as const,
  );
  if (meeting[0] === undefined) return 'NONE';

  let reached = start;
  for (const [from, to] of meeting) {
    if (from > reached) return 'PARTIAL';
    reached = Math.max(reached, to);
  }
  return reached >= end ? 'FULL' : 'PARTIAL';
};
